import { useState, useContext, useEffect, useMemo } from 'react';
import { AuthContext, User } from 'Auth';

import { toast } from 'react-toastify';

import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from 'fb';
import { createStudio, deleteStudio } from 'backend/db';

import Loading from 'components/Loading';
import { InputImagePreview } from 'components/InputImage/InputImagePreview';
import { InputImageUpdate } from 'components/InputImage/InputImageUpdate';
import ContainerProfile from './component/ContainerProfile';

export default function ProfileSettingStudio() {
    const user: User = useContext(AuthContext);

    const [loading, setLoading] = useState<boolean>(false);
    const [pending, setPending] = useState<boolean>(false);
    const [data, setData] = useState<any[]>([]);

    useEffect(() => {
        if (user.uid === '') {
            return;
        }

        const q = query(collection(db, 'users', user.uid, 'studio'), orderBy("createdAt"));

        setLoading(true);
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const studios: any[] = [];
            querySnapshot.forEach((doc) => {
                studios.push({
                    id: doc.id,
                    ...doc.data()
                });
            });
            setData(studios);
            setLoading(false);
        }, () => {
            setLoading(false);
        });
        return () => unsubscribe();
    }, [user]);

    const onClick = async () => {
        if (!pending) {
            try {
                setPending(true);
                await fetch(`https://api.crafttatva.com/artists/${user.uid}/studio`, {
                    method: "GET",
                    headers: {
                        "X-bypass-Cache": "true",
                    },
                });
                setPending(false);
            } catch(e) {
                setPending(false);
            }
        }
    }

    // Generate studio id for Firestore document
    const studioId = useMemo(() => {
        const l = data.length;
        const date = +new Date();
        // Random character
        const r = Math.random().toString(36).substring(7);
        return `${r}${l}${date}`;
    }, [data]);

    return (
        <ContainerProfile>
            {loading && <Loading />}
            <div>
                <h4 className="text-2xl font-medium mb-6">Studio</h4>
                <div className='grid grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6'>
                    {data.map((item) => (
                        <InputImagePreview
                            key={item.id}
                            value={item.image}
                            onDelete={async () => {
                                try {
                                    await deleteStudio(user.uid, item.id);
                                } catch (e: any) {
                                    toast.error(e.message ?? 'Something went wrong');
                                }
                            }}
                        />
                    ))}
                    <InputImageUpdate
                        onChange={async (image) => {
                            try {
                                await createStudio(user.uid, studioId,  { image: image });
                            } catch (e: any) {
                                toast.error(e.message ?? 'Something went wrong');
                            }
                        }}
                        customMetadata={{
                            studio: 'true',
                            userId: user.uid,
                            studioId: studioId,
                        }}
                    />
                </div>
                <button
                    type="submit"
                    onClick={onClick}
                    className="mt-6 w-full text-white bg-red-700 hover:bg-red-800 focus:outline-none focus:ring-0 focus:ring-red-300 font-medium rounded-full text-sm px-5 py-2.5 text-center dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-900"
                >
                    {pending ? (
                        <svg aria-hidden="true" role="status" className="inline w-4 h-4 me-3 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
                        </svg>
                    ) : "Save"}
                </button>
            </div>
        </ContainerProfile>
    );
}